import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle, CheckCircle2, Clock, Download, Truck } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { subscribeToOrders, formatOrderNumber, type Order, type OrderLineItem } from '../../../utils/firebase';
import { getBookDownloadUrl } from '../../../utils/downloads';
import { shopData } from './shopData';
import { bookData } from './bookData';
function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}
function normalizeProductName(value: string | undefined): string {
  return (value ?? '').toLowerCase().replace(/(\d+)/g, '').split(/[^a-z]+/).filter(Boolean).sort().join('');
}
function getItemImage(item: OrderLineItem): string | undefined {
  const pid = item.productId;
  const normalizedName = normalizeProductName(item.name);
  if (item.type === 'book') {
    const book = bookData.find(b => b.id === pid) ?? bookData.find(b => b.lookupKey && b.lookupKey === pid) ?? bookData.find(b => b.stripePriceId && b.stripePriceId === pid) ?? bookData.find(b => normalizeProductName(b.title) === normalizedName);
    return book?.coverImage;
  }
  const product = shopData.find(s => s.id === pid) ?? shopData.find(s => s.lookupKey && s.lookupKey === pid) ?? shopData.find(s => {
    const catalogName = normalizeProductName(s.name);
    return catalogName === normalizedName || catalogName.startsWith(normalizedName) || normalizedName.startsWith(catalogName);
  }) ?? (normalizedName === 'omega' ? shopData.find(s => s.id === 'omega-3') : undefined);
  return product?.image;
}
function getItemShopId(item: OrderLineItem): string | undefined {
  const pid = item.productId;
  const normalizedName = normalizeProductName(item.name);
  if (item.type === 'book') return undefined;
  const product = shopData.find(s => s.id === pid) ?? shopData.find(s => s.lookupKey && s.lookupKey === pid) ?? shopData.find(s => {
    const catalogName = normalizeProductName(s.name);
    return catalogName === normalizedName || catalogName.startsWith(normalizedName) || normalizedName.startsWith(catalogName);
  }) ?? (normalizedName === 'omega' ? shopData.find(s => s.id === 'omega-3') : undefined);
  return product?.id;
}
function FulfillmentStatus({
  order
}: {
  order: Order;
}) {
  if (order.fulfillmentStatus === 'delivered' || order.fulfillmentStatus === 'digital_delivered') {
    return <span className="inline-flex items-center gap-2 text-sm font-bold text-tealslate" data-id="element-2007" data-mp="420n1uxujs6l">
        <CheckCircle2 className="w-4 h-4" data-id="element-2008" data-mp="43v591hqh66z" />
        Delivered
      </span>;
  }
  if (order.fulfillmentStatus === 'shipped') {
    return <span className="inline-flex items-center gap-2 text-sm font-bold text-tealslate" data-id="element-2009" data-mp="2ckimfbkkj3pz">
        <Truck className="w-4 h-4" data-id="element-2010" data-mp="28j6v91m1qafo" />
        Shipped
      </span>;
  }
  return <span className="inline-flex items-center gap-2 text-sm font-bold text-burgundy" data-id="element-2011" data-mp="23lf2y6hhomay">
      <Clock className="w-4 h-4" data-id="element-2012" data-mp="30b5zscd96ljx" />
      Processing
    </span>;
}
function DownloadButton({
  orderId
}: {
  orderId: string;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleDownload = async () => {
    setLoading(true);
    setError(null);
    try {
      const {
        url
      } = await getBookDownloadUrl(orderId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setError(err?.message ?? 'Download failed. Try again.');
    } finally {
      setLoading(false);
    }
  };
  return <div data-id="element-2013" data-mp="1aykk1e0bluzl">
      <button type="button" onClick={handleDownload} disabled={loading} className="px-5 py-3 bg-tealslate text-white text-xs font-bold uppercase tracking-wider hover:bg-navyslate disabled:opacity-60 transition-colors inline-flex items-center gap-2 rounded-md" data-id="element-2014" data-mp="2ysxgofugb9x2">
        <Download className="w-4 h-4" data-id="element-2015" data-mp="2ur2k1ongtkpm" />
        {loading ? 'Preparing...' : 'Download'}
      </button>

      {error && <p className="text-xs text-burgundy mt-2" data-id="element-2016" data-mp="3ign5qfb06w2l">{error}</p>}
    </div>;
}
export function AccountOrderDetail() {
  const {
    orderId
  } = useParams<{
    orderId: string;
  }>();
  const {
    user,
    loading: authLoading
  } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', {
        state: {
          from: `/account/orders/${orderId}`
        },
        replace: true
      });
    }
  }, [user, authLoading, navigate, orderId]);
  useEffect(() => {
    if (!user || !orderId) return;
    setOrdersLoading(true);
    const unsubscribe = subscribeToOrders(user.uid, orders => {
      const matchingOrder = orders.find(candidate => candidate.id === orderId);
      if (matchingOrder) {
        setOrder(matchingOrder);
        setNotFound(false);
      } else {
        setOrder(null);
        setNotFound(true);
      }
      setOrdersLoading(false);
    });
    return unsubscribe;
  }, [user, orderId]);
  if (authLoading || ordersLoading || !user) {
    return <div className="w-full min-h-screen bg-white flex items-center justify-center" data-id="element-2017" data-mp="1klq0wiy81w72">
        <span className="w-6 h-6 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-2018" data-mp="1ft6mxkvmgz1q" />
      </div>;
  }
  if (notFound || !order) {
    return <div className="w-full min-h-screen bg-white pt-32 pb-24 px-6" data-id="element-2019" data-mp="n5u49lsl1bdp">
        <div className="max-w-4xl mx-auto" data-id="element-2020" data-mp="n3pzl1mv08cd">
          <Link to="/account" className="inline-flex items-center gap-2 text-sm font-bold text-tealslate hover:text-navyslate mb-8" data-id="element-2021" data-mp="2n8lgcmov2mch">
            <ArrowLeft className="w-4 h-4" data-id="element-2022" data-mp="2wxmt84040nm8" />
            Back to Order History
          </Link>

          <div className="bg-white border border-[#E5E5E5] rounded-md p-10 text-center" data-id="element-2023" data-mp="31wq0zw7h57xw">
            <AlertCircle className="w-10 h-10 text-burgundy mx-auto mb-4" data-id="element-2024" data-mp="31yiwqggju7r4" />
            <h1 className="text-2xl font-bold text-[#111111] mb-2" data-id="element-2025" data-mp="24asd1jabsaek">
              Order not found
            </h1>
            <p className="text-sm text-[#5A5A5A]" data-id="element-2026" data-mp="29a5127cxnniq">
              This order could not be found in your account.
            </p>
          </div>
        </div>
      </div>;
  }
  return <div className="w-full min-h-screen bg-white pt-32 pb-24 px-6" data-id="element-2027" data-mp="2ia1vkpm07glp">
      <div className="max-w-5xl mx-auto" data-id="element-2028" data-mp="3pl8zxl4mw8db">

        <Link to="/account" className="inline-flex items-center gap-2 text-sm font-bold text-tealslate hover:text-navyslate transition-colors mb-8" data-id="element-2029" data-mp="3pon1ir5h4djp">
          <ArrowLeft className="w-4 h-4" data-id="element-2030" data-mp="1hzr00bbe0m3m" />
          Back to Order History
        </Link>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8" data-id="element-2031" data-mp="1dzgvl338u3qv">
          <div data-id="element-2032" data-mp="196xgu2stl2kh">
            <p className="text-xs font-bold uppercase tracking-wider text-[#111111] mb-2" data-id="element-2033" data-mp="196e02pewok81">
              Order #{formatOrderNumber(order.id)}
            </p>

            <h1 className="text-3xl md:text-4xl font-bold text-[#111111] tracking-tight mb-3" data-id="element-2034" data-mp="zaaxxjk20ll3">
              Order Details
            </h1>

            <p className="text-sm text-[#5A5A5A]" data-id="element-2035" data-mp="u9mxy8dukueq">
              Placed {formatDate(order.createdAt)}
            </p>
          </div>

          <FulfillmentStatus order={order} data-id="element-2036" data-mp="lajcwyv4e4xl" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2037" data-mp="lc47uplcjpvz">

          <div className="lg:col-span-2 space-y-6" data-id="element-2038" data-mp="2knnid8mat67r">

            <div className="bg-white border border-[#E5E5E5] rounded-md p-6 md:p-8" data-id="element-2039" data-mp="2kl1b7a3bksq2">
              <h2 className="text-xl font-bold text-[#111111] mb-6" data-id="element-2040" data-mp="2fp2ijaaykoju">
                Items
              </h2>

              <ul className="divide-y divide-[#E5E5E5]" data-id="element-2041" data-mp="1olavw02oli5c">
                {order.lineItems.map((item, index) => {
                const image = getItemImage(item);
                return <li key={`${item.productId}-${index}`} className="py-6 first:pt-0 last:pb-0 flex gap-6" data-id="element-2042" data-mp="1er11kcu6i9rz" data-mpi={index}>
                      {image && <div className="w-24 h-24 sm:w-32 sm:h-32 shrink-0 flex items-center justify-center" data-id="element-2043" data-mp="19u2bc5dmymz2">
                          <img src={image} alt={item.name} className="max-w-full max-h-full object-contain" data-id="element-2044" data-mp="vuj2tbgwx9m8" />
                        </div>}

                      <div className="flex-1" data-id="element-2045" data-mp="2q67khlej3qab">
                        {item.type === 'book' ? <Link to="/shop" className="font-bold text-[#0056b3] hover:text-[#004494] hover:underline text-lg mb-2 block transition-colors" data-id="element-2046" data-mp="2v5apx6yf7yit">
                            {item.name}
                          </Link> : <Link to={`/shop/${getItemShopId(item) || item.productId}`} className="font-bold text-[#0056b3] hover:text-[#004494] hover:underline text-lg mb-2 block transition-colors" data-id="element-2047" data-mp="tawh6llv29k8">
                            {item.name}
                          </Link>}

                        <p className="text-sm text-[#5A5A5A] mb-1" data-id="element-2048" data-mp="xaetwu3j3ebz">
                          Quantity: {item.quantity}
                        </p>

                        <p className="text-sm text-[#5A5A5A] mb-4" data-id="element-2049" data-mp="1c6cuqpoed6uj">
                          {formatPrice(item.unitPrice * item.quantity)}
                        </p>

                        {item.type === 'book' && order.status === 'paid' && <DownloadButton orderId={order.id} data-id="element-2050" data-mp="392ture014ww2" />}
                      </div>
                    </li>;
              })}
              </ul>
            </div>

            {order.shippingAddress?.line1 && <div className="bg-white border border-[#E5E5E5] rounded-md p-6 md:p-8" data-id="element-2051" data-mp="dycumod3bnzm">
                <h2 className="text-xl font-bold text-[#111111] mb-5" data-id="element-2052" data-mp="90l0dnoz3ps9">
                  Shipping Information
                </h2>

                <div className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-2053" data-mp="16migxfjsll1k">
                  {order.shippingAddress.recipientName && <p className="font-bold text-[#111111]" data-id="element-2054" data-mp="11r33gopp5r5o">
                      {order.shippingAddress.recipientName}
                    </p>}

                  <p data-id="element-2055" data-mp="rye5mwn2kbst">{order.shippingAddress.line1}</p>

                  {order.shippingAddress.line2 && <p data-id="element-2056" data-mp="1pfmaz20x9zwp">{order.shippingAddress.line2}</p>}

                  <p data-id="element-2057" data-mp="1kmjnnbmqnmoy">
                    {order.shippingAddress.city},{' '}
                    {order.shippingAddress.state}{' '}
                    {order.shippingAddress.postalCode}
                  </p>

                  <p data-id="element-2058" data-mp="1wylwbd7y21ip">{order.shippingAddress.country}</p>
                </div>

                {order.shippingAddress.deliveryInstructions && <div className="mt-6 pt-6 border-t border-[#E5E5E5]" data-id="element-2059" data-mp="26w0af66eae7u">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#9A9A90] mb-2" data-id="element-2060" data-mp="26xb9wtst4zt5">
                      Delivery Instructions
                    </p>

                    <p className="text-sm text-[#5A5A5A]" data-id="element-2061" data-mp="2fvn2v2h5c9or">
                      {order.shippingAddress.deliveryInstructions}
                    </p>
                  </div>}
              </div>}

            {order.trackingNumber && <div className="bg-white border border-[#E5E5E5] rounded-md p-6 md:p-8" data-id="element-2062" data-mp="2ulujrdri9knc">
                <h2 className="text-xl font-bold text-[#111111] mb-4" data-id="element-2063" data-mp="2zkp26elk976x">
                  Tracking
                </h2>

                <p className="text-sm text-[#5A5A5A] flex items-center gap-2" data-id="element-2064" data-mp="29w0e2qznwers">
                  <Truck className="w-4 h-4" data-id="element-2065" data-mp="24xgtatwt1910" />
                  {order.trackingCarrier ?? 'Tracking'} ·{' '}
                  {order.trackingNumber}
                </p>
              </div>}
          </div>

          <div data-id="element-2066" data-mp="2ev3ajn1ktco5">
            <div className="bg-white border border-[#E5E5E5] rounded-md p-6 sticky top-28" data-id="element-2067" data-mp="2eulh0y6atcjx">
              <h2 className="text-xl font-bold text-[#111111] mb-6" data-id="element-2068" data-mp="2sm8civsf2h1n">
                Order Summary
              </h2>

              <div className="space-y-4 text-sm" data-id="element-2069" data-mp="2nn589dk9geuq">
                <div className="flex justify-between text-[#5A5A5A]" data-id="element-2070" data-mp="32l79vi7qeq9c">
                  <span data-id="element-2071" data-mp="32kfpl3jl4eyd">Subtotal</span>
                  <span data-id="element-2072" data-mp="2xme0a0vkfjeg">{formatPrice(order.subtotal)}</span>
                </div>

                {(order.shipping ?? 0) > 0 && <div className="flex justify-between text-[#5A5A5A]" data-id="element-2073" data-mp="187zcbisdg0k0">
                    <span data-id="element-2074" data-mp="138mp2wxl8rgw">Shipping</span>
                    <span data-id="element-2075" data-mp="139o6dj576a72">{formatPrice(order.shipping ?? 0)}</span>
                  </div>}

                <div className="flex justify-between text-[#5A5A5A]" data-id="element-2076" data-mp="3ew8cjtqs38s8">
                  <span data-id="element-2077" data-mp="3jti3jdttfsi6">Tax</span>
                  <span data-id="element-2078" data-mp="3jwmb8l6hp6dl">{formatPrice(order.tax ?? 0)}</span>
                </div>

                {(order.discount ?? 0) > 0 && <div className="flex justify-between text-[#5A5A5A]" data-id="element-2079" data-mp="3a4gnjnn9tb47">
                    <span data-id="element-2080" data-mp="3a2dmm8kkxxkp">Discount</span>
                    <span data-id="element-2081" data-mp="2w4pc9uo528tw">-{formatPrice(order.discount ?? 0)}</span>
                  </div>}

                <div className="pt-4 border-t border-[#E5E5E5] flex justify-between font-bold text-[#111111] text-base" data-id="element-2082" data-mp="307lnv1f9h4oc">
                  <span data-id="element-2083" data-mp="2m8c9m7am8kn0">Grand Total</span>
                  <span data-id="element-2084" data-mp="2r5ugzt8zirsw">{formatPrice(order.total)}</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>;
}